import React, { useContext, useState } from 'react';
import bg from 'assets/images/auth/credentials3.jpg';
import { Link } from 'react-router-dom';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import zxcvbn from 'zxcvbn'; // ← import here
import Alert from 'components/alerts/WarningAlert';
import { AlertsContext } from 'context/AlertsContext';
import { AuthContext } from 'context/AuthContext';
import TnC from 'assets/docs/tnc.pdf';
import logo from 'assets/images/logos/Logo_1.png';
import { Button } from 'antd';

const Credentials = ({
  state,
  alert,
  submit_credentials,
  toggleAcceptTerms,
  handleChange,
  togglePassword,
  toggleConfirmPassword,
}) => {
  const { showAlert } = useContext(AlertsContext);
  const { setCredentials } = useContext(AuthContext);
  const { message, variant } = alert;

  // Local state for password strength feedback
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    warning: '',
    suggestions: [],
    crackTime: '',
  });

  const handlePasswordInputChange = (e) => {
    const value = e.target.value;

    // Update formik/your state
    handleChange(e);

    if (value.length > 0) {
      // Optional: pass username (without @domain) as extra dictionary word
      const userInputs = state.username ? [state.username.split('@')[0]] : [];

      const result = zxcvbn(value, userInputs);

      setPasswordStrength({
        score: result.score,
        warning: result.feedback.warning || '',
        suggestions: result.feedback.suggestions || [],
        crackTime:
          result.crack_times_display?.offline_slow_hashing_1e4_per_second ||
          'unknown',
      });
    } else {
      setPasswordStrength({
        score: 0,
        warning: '',
        suggestions: [],
        crackTime: '',
      });
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={state}
      validationSchema={Yup.object().shape({
        password: Yup.string()
          .min(8, 'Password should be at least 8 characters (15+ is much stronger)')
          .test(
            'password-strength',
            'Password is too weak or too common',
            function (value) {
              if (!value) return true;
              const result = zxcvbn(value);
              return result.score >= 2; // 0=very weak, 1=weak, 2=fair, 3=good, 4=strong
            }
          )
          .required('Password is required'),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref('password')], 'Passwords do not match')
          .required('Confirm password is required'),
        tnc: Yup.boolean().oneOf([true], 'Please accept Terms and Conditions'),
      })}
      onSubmit={() => submit_credentials()}
    >
      {({ errors, touched }) => (
        <div className="d-md-flex d-sm-block">
          <div className="m-auto">
            <img src={logo} alt="Logo" className="d-block d-md-none wd-100 ht-100" />
          </div>

          <div className="col-md-12 col-sm-12" id="credentials-background">
            <div
              style={{
                minHeight: '100%',
                paddingBottom: '40px',
                marginBottom: '-40px',
              }}
            >
              <div className="container">
                <Link to="/" className="">
                  <img
                    src={logo}
                    style={{
                      height: '30px',
                      width: 'auto',
                      position: 'absolute',
                      top: '20px',
                    }}
                    alt="Logo"
                  />
                </Link>

                <div
                  className=""
                  style={{
                    position: 'fixed',
                    right: '40px',
                    bottom: '0',
                  }}
                >
                  <a href={TnC} rel="noopener noreferrer" target="_blank" className="text-white">
                    Terms and conditions |
                  </a>
                  <Link to="#" className="text-white">
                    {' '}
                    &copy; {new Date().getFullYear()} Nexus
                  </Link>
                </div>

                <div
                  className="card"
                  style={{
                    position: 'absolute',
                    top: '8%',
                    right: '10%',
                    borderRadius: '15px 30px',
                    maxWidth: '450px',
                  }}
                >
                  <div className="card-body p-5">
                    <h2 className="mb-5 custom-text-primary">Credentials</h2>
                    {showAlert && <Alert message={message} variant={variant} />}
                    <Form className="login-form clearfix">
                      <div className="form-group">
                        <label htmlFor="username">Username (email)</label>
                        <input
                          name="username"
                          type="email"
                          value={state.username}
                          onChange={handleChange}
                          className={
                            'form-control border' +
                            (errors.username && touched.username ? ' is-invalid' : '')
                          }
                        />
                        <ErrorMessage
                          name="username"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="form-group position-relative">
                        <label htmlFor="password">Password</label>
                        <input
                          name="password"
                          type={state.showPassword ? 'text' : 'password'}
                          value={state.password}
                          onChange={handlePasswordInputChange} // ← updated
                          className={
                            'form-control border password-input' +
                            (errors.password && touched.password ? ' is-invalid' : '')
                          }
                        />
                        <i
                          className={`fa ${
                            state.showPassword ? 'fa-eye' : 'fa-eye-slash'
                          } password-icon`}
                          onClick={togglePassword}
                          style={{
                            position: 'absolute',
                            right: '15px',
                            top: '38px',
                            cursor: 'pointer',
                            zIndex: 10,
                          }}
                        />

                        {state.password && (
                          <div className="mt-2">
                            {/* Strength bar */}
                            <div className="progress" style={{ height: '8px' }}>
                              <div
                                className={`progress-bar ${
                                  passwordStrength.score === 0
                                    ? 'bg-danger'
                                    : passwordStrength.score === 1
                                    ? 'bg-warning'
                                    : passwordStrength.score === 2
                                    ? 'bg-info'
                                    : 'bg-success'
                                }`}
                                role="progressbar"
                                style={{ width: `${(passwordStrength.score + 1) * 20}%` }}
                                aria-valuenow={passwordStrength.score}
                                aria-valuemin="0"
                                aria-valuemax="4"
                              ></div>
                            </div>

                            <div className="small mt-1">
                              {passwordStrength.score === 0 && (
                                <span className="text-danger">Very weak – easily cracked</span>
                              )}
                              {passwordStrength.score === 1 && (
                                <span className="text-warning">Weak</span>
                              )}
                              {passwordStrength.score === 2 && (
                                <span className="text-info">Fair</span>
                              )}
                              {passwordStrength.score === 3 && (
                                <span className="text-success">Good</span>
                              )}
                              {passwordStrength.score === 4 && (
                                <span className="text-success fw-bold">Strong</span>
                              )}

                              {passwordStrength.warning && (
                                <div className="text-danger mt-1">{passwordStrength.warning}</div>
                              )}

                              {passwordStrength.suggestions.length > 0 && (
                                <ul className="mt-1 mb-1 pl-3 small">
                                  {passwordStrength.suggestions.map((s, i) => (
                                    <li key={i}>{s}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        )}

                        <ErrorMessage
                          name="password"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="form-group position-relative">
                        <label htmlFor="password">Confirm Password</label>
                        <input
                          name="confirmPassword"
                          type={state.showConfirmPassword ? 'text' : 'password'}
                          value={state.confirmPassword}
                          onChange={handleChange}
                          className={
                            'form-control border' +
                            (errors.confirmPassword && touched.confirmPassword
                              ? ' is-invalid'
                              : '')
                          }
                        />
                        <i
                          className={`fa ${
                            state.showConfirmPassword ? 'fa-eye' : 'fa-eye-slash'
                          } password-icon`}
                          onClick={toggleConfirmPassword}
                          style={{
                            position: 'absolute',
                            right: '15px',
                            top: '38px',
                            cursor: 'pointer',
                            zIndex: 10,
                          }}
                        />
                        <ErrorMessage
                          name="confirmPassword"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      <div className="form-group">
                        <input
                          type="checkbox"
                          name="tnc"
                          checked={state.tnc}
                          onChange={toggleAcceptTerms}
                          className={
                            'form-check-input' +
                            (errors.tnc && touched.tnc ? ' is-invalid' : '')
                          }
                        />
                        <label className="form-check-label">
                          I have read and accept the{' '}
                          <a href={TnC} rel="noopener noreferrer" target="_blank">
                            Terms and conditions
                          </a>
                        </label>
                        <ErrorMessage name="tnc" component="div" className="invalid-feedback" />
                      </div>

                      <Button
                        htmlType="submit"
                        disabled={state.loading}
                        className="btn mt-3 px-5 btn-login btn-block"
                      >
                        {state.loading && (
                          <i
                            className="fa fa-circle-notch fa-spin"
                            style={{ marginRight: '5px' }}
                          />
                        )}
                        {state.loading && (
                          <span className="text-capitalize">Please wait...</span>
                        )}
                        {!state.loading && <span className="text-capitalize"> Submit</span>}
                      </Button>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
};

export default Credentials;